// Self-contained "Access readers" section embedded in BuildingFormPage's
// edit view. Mirrors the page-level idioms (loading/error/empty/data,
// LayerCard + Table, toast on mutation errors) but scoped to a section
// rather than a full page — see WifiCredentialsPage's SetupInstructions /
// WifiNetworksPage's DeleteResource+ConfirmDialog usage for the patterns
// this follows.
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Banner,
  Button,
  ClipboardText,
  LayerDialog,
  Empty,
  Input,
  Label,
  LayerCard,
  Loader,
  Select,
  Table,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import {
  ArrowClockwiseIcon,
  DoorOpenIcon,
  PencilSimpleIcon,
  PlusIcon,
  PowerIcon,
  TrashIcon,
  UsersIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { ApiError } from '../../api/client'
import {
  createAccessReader,
  deleteAccessReader,
  listAccessReaders,
  resyncAccessReader,
  updateAccessReader,
  type AccessReaderDto,
  type AccessReaderStatus,
  type AccessVendor,
} from '../../api/accessReaders'
import { formatDateTime } from '../../lib/format'
import { useTranslation } from '../../i18n'
import { ConfirmDialog } from '../ConfirmDialog'

interface HeaderPair {
  key: string
  value: string
}

interface ReaderFormState {
  name: string
  vendor: AccessVendor
  unitId: string
  baseUrl: string
  username: string
  password: string
  portalId: string
  headers: HeaderPair[]
}

function emptyForm(): ReaderFormState {
  return {
    name: '',
    vendor: 'truesafe',
    unitId: '',
    baseUrl: '',
    username: '',
    password: '',
    portalId: '1',
    headers: [],
  }
}

/** Prefills the edit form from an existing reader's redacted DTO config. */
function formFromReader(reader: AccessReaderDto): ReaderFormState {
  const config = reader.config
  const headers =
    config.headers && typeof config.headers === 'object'
      ? Object.entries(config.headers as Record<string, string>).map(([key, value]) => ({
          key,
          value,
        }))
      : []
  return {
    name: reader.name,
    vendor: reader.vendor,
    unitId: typeof config.unitId === 'number' ? String(config.unitId) : '',
    baseUrl: typeof config.baseUrl === 'string' ? config.baseUrl : '',
    username: typeof config.username === 'string' ? config.username : '',
    password: '',
    portalId: typeof config.portalId === 'number' ? String(config.portalId) : '1',
    headers,
  }
}

/** Builds the API config payload from form state, per vendor. */
function buildConfig(form: ReaderFormState, { includePassword }: { includePassword: boolean }) {
  if (form.vendor === 'truesafe') {
    return { unitId: Number(form.unitId) }
  }
  const headers = form.headers.reduce<Record<string, string>>((acc, h) => {
    if (h.key.trim()) acc[h.key.trim()] = h.value
    return acc
  }, {})
  return {
    baseUrl: form.baseUrl,
    username: form.username,
    ...(includePassword && form.password ? { password: form.password } : {}),
    portalId: Number(form.portalId || '1'),
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
  }
}

export function AccessReadersSection({ buildingId }: { buildingId: string }) {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const navigate = useNavigate()

  const [readers, setReaders] = useState<AccessReaderDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<ReaderFormState>(emptyForm)
  const [creating, setCreating] = useState(false)

  const [tokenDialogOpen, setTokenDialogOpen] = useState(false)
  const [deviceToken, setDeviceToken] = useState<string | null>(null)

  const [editTarget, setEditTarget] = useState<AccessReaderDto | null>(null)
  const [editForm, setEditForm] = useState<ReaderFormState | null>(null)
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<AccessReaderDto | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [resyncingId, setResyncingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      setReaders(await listAccessReaders(buildingId, token))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [buildingId, getAccessTokenSilently, t])

  useEffect(() => {
    void load()
  }, [load])

  function reportError(err: unknown) {
    toasts.add({
      variant: 'error',
      title: t('common.errorTitle'),
      description: err instanceof ApiError ? err.message : t('common.error'),
    })
  }

  function openCreate() {
    setCreateForm(emptyForm())
    setCreateOpen(true)
  }

  function handleCreateOpenChange(open: boolean) {
    setCreateOpen(open)
  }

  async function handleCreateSubmit(e: FormEvent) {
    e.preventDefault()
    setCreating(true)
    try {
      const token = await getAccessTokenSilently()
      const result = await createAccessReader(
        buildingId,
        {
          name: createForm.name,
          vendor: createForm.vendor,
          config: buildConfig(createForm, { includePassword: true }),
        },
        token,
      )
      setReaders((prev) => [...prev, result.reader])
      setCreateOpen(false)
      toasts.add({ variant: 'success', title: t('accessReaders.createSuccess') })
      if (result.deviceToken) {
        setDeviceToken(result.deviceToken)
        setTokenDialogOpen(true)
      }
    } catch (err) {
      reportError(err)
    } finally {
      setCreating(false)
    }
  }

  function handleTokenDialogChange(open: boolean) {
    setTokenDialogOpen(open)
    if (!open) setDeviceToken(null)
  }

  function openEdit(reader: AccessReaderDto) {
    setEditTarget(reader)
    setEditForm(formFromReader(reader))
  }

  function handleEditOpenChange(open: boolean) {
    if (!open) {
      setEditTarget(null)
      setEditForm(null)
    }
  }

  async function handleEditSubmit(e: FormEvent) {
    e.preventDefault()
    if (!editTarget || !editForm) return
    setSaving(true)
    try {
      const token = await getAccessTokenSilently()
      const updated = await updateAccessReader(
        buildingId,
        editTarget.id,
        { name: editForm.name, config: buildConfig(editForm, { includePassword: true }) },
        token,
      )
      setReaders((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
      setEditTarget(null)
      setEditForm(null)
      toasts.add({ variant: 'success', title: t('accessReaders.updateSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleStatus(reader: AccessReaderDto) {
    const nextStatus: AccessReaderStatus = reader.status === 'active' ? 'disabled' : 'active'
    setTogglingId(reader.id)
    try {
      const token = await getAccessTokenSilently()
      const updated = await updateAccessReader(buildingId, reader.id, { status: nextStatus }, token)
      setReaders((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
      toasts.add({ variant: 'success', title: t('accessReaders.updateSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setTogglingId(null)
    }
  }

  async function handleResync(reader: AccessReaderDto) {
    setResyncingId(reader.id)
    try {
      const token = await getAccessTokenSilently()
      await resyncAccessReader(buildingId, reader.id, token)
      toasts.add({ variant: 'success', title: t('accessReaders.resyncSuccess') })
      await load()
    } catch (err) {
      reportError(err)
    } finally {
      setResyncingId(null)
    }
  }

  async function handleDelete(reader: AccessReaderDto) {
    setDeleting(true)
    try {
      const token = await getAccessTokenSilently()
      await deleteAccessReader(buildingId, reader.id, token)
      setReaders((prev) => prev.filter((r) => r.id !== reader.id))
      setDeleteTarget(null)
      toasts.add({ variant: 'success', title: t('accessReaders.deleteSuccess') })
    } catch (err) {
      reportError(err)
    } finally {
      setDeleting(false)
    }
  }

  function addHeaderRow(setForm: (updater: (f: ReaderFormState) => ReaderFormState) => void) {
    setForm((f) => ({ ...f, headers: [...f.headers, { key: '', value: '' }] }))
  }

  function updateHeaderRow(
    setForm: (updater: (f: ReaderFormState) => ReaderFormState) => void,
    index: number,
    field: 'key' | 'value',
    value: string,
  ) {
    setForm((f) => {
      const current = f.headers[index] ?? { key: '', value: '' }
      const updated: HeaderPair =
        field === 'key' ? { ...current, key: value } : { ...current, value }
      const headers = [...f.headers]
      headers[index] = updated
      return { ...f, headers }
    })
  }

  function removeHeaderRow(
    setForm: (updater: (f: ReaderFormState) => ReaderFormState) => void,
    index: number,
  ) {
    setForm((f) => ({ ...f, headers: f.headers.filter((_, i) => i !== index) }))
  }

  function renderConfigFields(
    form: ReaderFormState,
    setForm: (updater: (f: ReaderFormState) => ReaderFormState) => void,
    { vendorEditable }: { vendorEditable: boolean },
  ) {
    return (
      <>
        {vendorEditable && (
          <div className="flex flex-col gap-1">
            <Label>{t('accessReaders.form.vendor')}</Label>
            <Select
              aria-label={t('accessReaders.form.vendor')}
              value={form.vendor}
              onValueChange={(v) => v && setForm((f) => ({ ...f, vendor: v as AccessVendor }))}
            >
              <Select.Option value="truesafe">{t('accessReaders.vendor.truesafe')}</Select.Option>
              <Select.Option value="controlid">{t('accessReaders.vendor.controlid')}</Select.Option>
            </Select>
          </div>
        )}

        {form.vendor === 'truesafe' && (
          <div className="flex flex-col gap-1">
            <Label>{t('accessReaders.form.unitId')}</Label>
            <Input
              aria-label={t('accessReaders.form.unitId')}
              type="number"
              value={form.unitId}
              onChange={(e) => setForm((f) => ({ ...f, unitId: e.target.value }))}
              required
            />
          </div>
        )}

        {form.vendor === 'controlid' && (
          <>
            <div className="flex flex-col gap-1">
              <Label>{t('accessReaders.form.baseUrl')}</Label>
              <Input
                aria-label={t('accessReaders.form.baseUrl')}
                type="url"
                value={form.baseUrl}
                onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('accessReaders.form.username')}</Label>
              <Input
                aria-label={t('accessReaders.form.username')}
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('accessReaders.form.password')}</Label>
              <Input
                aria-label={t('accessReaders.form.password')}
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={t('accessReaders.form.passwordPlaceholder')}
                required={vendorEditable}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('accessReaders.form.portalId')}</Label>
              <Input
                aria-label={t('accessReaders.form.portalId')}
                type="number"
                value={form.portalId}
                onChange={(e) => setForm((f) => ({ ...f, portalId: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t('accessReaders.form.headers')}</Label>
              {form.headers.map((h, i) => (
                <div key={i} className="flex items-end gap-2">
                  <Input
                    aria-label={t('accessReaders.form.headerKey')}
                    placeholder={t('accessReaders.form.headerKey')}
                    value={h.key}
                    onChange={(e) => updateHeaderRow(setForm, i, 'key', e.target.value)}
                  />
                  <Input
                    aria-label={t('accessReaders.form.headerValue')}
                    placeholder={t('accessReaders.form.headerValue')}
                    value={h.value}
                    onChange={(e) => updateHeaderRow(setForm, i, 'value', e.target.value)}
                  />
                  <Button
                    variant="secondary-destructive"
                    size="sm"
                    type="button"
                    onClick={() => removeHeaderRow(setForm, i)}
                  >
                    {t('accessReaders.form.removeHeader')}
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => addHeaderRow(setForm)}
              >
                {t('accessReaders.form.addHeader')}
              </Button>
            </div>
          </>
        )}
      </>
    )
  }

  /** controlid-only sync-health chip — truesafe readers never sync a directory, so callers skip rendering this entirely for them. */
  function renderSyncHealth(reader: AccessReaderDto) {
    if (reader.lastSyncError) {
      const title = reader.lastSyncOkAt
        ? `${reader.lastSyncError} (${t('accessReaders.syncHealth.lastOk', { time: formatDateTime(reader.lastSyncOkAt) })})`
        : reader.lastSyncError
      return (
        <span title={title}>
          <Badge variant="error">{t('accessReaders.syncHealth.error')}</Badge>
        </span>
      )
    }
    if (reader.lastSyncOkAt) {
      return (
        <Badge variant="success">
          {t('accessReaders.syncHealth.synced', { time: formatDateTime(reader.lastSyncOkAt) })}
        </Badge>
      )
    }
    return <Badge variant="neutral">{t('accessReaders.syncHealth.never')}</Badge>
  }

  return (
    <div className="flex flex-col gap-4">
      <LayerCard>
        <LayerCard.Primary>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Text as="h2" variant="heading2">
                {t('accessReaders.title')}
              </Text>
              <Button
                variant="primary"
                size="sm"
                icon={<PlusIcon size={16} />}
                onClick={openCreate}
              >
                {t('accessReaders.add')}
              </Button>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-16">
                <Loader size="lg" />
              </div>
            )}

            {!loading && error && (
              <Banner variant="error">
                {t('common.errorPrefix')} {error}
              </Banner>
            )}

            {!loading && !error && readers.length === 0 && (
              <Empty
                icon={<DoorOpenIcon size={48} className="text-kumo-inactive" />}
                title={t('accessReaders.empty')}
              />
            )}

            {!loading && !error && readers.length > 0 && (
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head>{t('accessReaders.name')}</Table.Head>
                      <Table.Head>{t('accessReaders.vendor')}</Table.Head>
                      <Table.Head>{t('accessReaders.status')}</Table.Head>
                      <Table.Head>{t('accessReaders.syncHealth.title')}</Table.Head>
                      <Table.Head>{t('accessReaders.actions')}</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {readers.map((reader) => (
                      <Table.Row key={reader.id} data-testid={`access-reader-row-${reader.id}`}>
                        <Table.Cell>{reader.name}</Table.Cell>
                        <Table.Cell>
                          <Badge variant="outline">
                            {t(`accessReaders.vendor.${reader.vendor}`)}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant={reader.status === 'active' ? 'success' : 'neutral'}>
                            {t(`accessReaders.status.${reader.status}`)}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {reader.vendor === 'controlid' ? renderSyncHealth(reader) : null}
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex gap-2">
                            {reader.vendor === 'controlid' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<UsersIcon size={16} />}
                                aria-label={t('accessReaders.deviceUsers.action')}
                                onClick={() =>
                                  navigate(
                                    `/buildings/${buildingId}/readers/${reader.id}/device-users`,
                                  )
                                }
                              />
                            )}
                            {reader.vendor === 'controlid' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<ArrowClockwiseIcon size={16} />}
                                aria-label={t('accessReaders.resync')}
                                loading={resyncingId === reader.id}
                                onClick={() => void handleResync(reader)}
                              />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<PencilSimpleIcon size={16} />}
                              aria-label={t('accessReaders.edit')}
                              onClick={() => openEdit(reader)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<PowerIcon size={16} />}
                              aria-label={
                                reader.status === 'active'
                                  ? t('accessReaders.disable')
                                  : t('accessReaders.enable')
                              }
                              loading={togglingId === reader.id}
                              onClick={() => void handleToggleStatus(reader)}
                            />
                            <Button
                              variant="secondary-destructive"
                              size="sm"
                              icon={<TrashIcon size={16} />}
                              aria-label={t('accessReaders.delete')}
                              onClick={() => setDeleteTarget(reader)}
                            />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        </LayerCard.Primary>
      </LayerCard>

      <LayerDialog.Root
        open={createOpen}
        onOpenChange={(next) => handleCreateOpenChange(next)}
        dismissDisabled={creating}
      >
        <LayerDialog.Content size="base">
          <LayerDialog.Title>{t('accessReaders.create.title')}</LayerDialog.Title>
          <LayerDialog.Body>
            <form
              id="access-reader-create-form"
              onSubmit={(e) => void handleCreateSubmit(e)}
              className="flex flex-col gap-3"
            >
              <div className="flex flex-col gap-1">
                <Label>{t('accessReaders.form.name')}</Label>
                <Input
                  aria-label={t('accessReaders.form.name')}
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              {renderConfigFields(createForm, setCreateForm, { vendorEditable: true })}
            </form>
          </LayerDialog.Body>
          <LayerDialog.Actions dismissLabel={t('common.cancel')}>
            <LayerDialog.Action form="access-reader-create-form" type="submit" loading={creating}>
              {t('common.save')}
            </LayerDialog.Action>
          </LayerDialog.Actions>
        </LayerDialog.Content>
      </LayerDialog.Root>

      <LayerDialog.Root
        open={tokenDialogOpen && Boolean(deviceToken)}
        onOpenChange={(next) => handleTokenDialogChange(next)}
      >
        <LayerDialog.Content size="sm" closeLabel={t('common.close')}>
          <LayerDialog.Title>{t('accessReaders.token.title')}</LayerDialog.Title>
          <LayerDialog.Body>
            <div className="flex flex-col gap-3">
              <Banner variant="alert" icon={<WarningCircleIcon />}>
                {t('accessReaders.token.warning')}
              </Banner>
              {deviceToken && <ClipboardText text={deviceToken} />}
              <div className="flex justify-end">
                <Button variant="primary" onClick={() => handleTokenDialogChange(false)}>
                  {t('accessReaders.token.closeButton')}
                </Button>
              </div>
            </div>
          </LayerDialog.Body>
        </LayerDialog.Content>
      </LayerDialog.Root>

      <LayerDialog.Root
        open={Boolean(editTarget && editForm)}
        onOpenChange={(next) => handleEditOpenChange(next)}
        dismissDisabled={saving}
      >
        <LayerDialog.Content size="base">
          <LayerDialog.Title>{t('accessReaders.edit.title')}</LayerDialog.Title>
          <LayerDialog.Body>
            {editForm && (
              <form
                id="access-reader-edit-form"
                onSubmit={(e) => void handleEditSubmit(e)}
                className="flex flex-col gap-3"
              >
                <div className="flex flex-col gap-1">
                  <Label>{t('accessReaders.form.name')}</Label>
                  <Input
                    aria-label={t('accessReaders.form.name')}
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, name: e.target.value } : f))}
                    required
                  />
                </div>
                {renderConfigFields(
                  editForm,
                  (updater) => setEditForm((f) => (f ? updater(f) : f)),
                  { vendorEditable: false },
                )}
              </form>
            )}
          </LayerDialog.Body>
          <LayerDialog.Actions dismissLabel={t('common.cancel')}>
            <LayerDialog.Action form="access-reader-edit-form" type="submit" loading={saving}>
              {t('common.save')}
            </LayerDialog.Action>
          </LayerDialog.Actions>
        </LayerDialog.Content>
      </LayerDialog.Root>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title={t('accessReaders.deleteConfirm.title', { name: deleteTarget?.name ?? '' })}
        description={t('accessReaders.deleteConfirm.description')}
        confirmLabel={t('accessReaders.delete')}
        variant="destructive"
        loading={deleting}
        onConfirm={() => (deleteTarget ? handleDelete(deleteTarget) : undefined)}
      />
    </div>
  )
}

AccessReadersSection.displayName = 'AccessReadersSection'
